"use client";
import type { SelectProps } from "antd";
import CardChevron from "@/Components/Common/CardChevron";
import { Checkbox, Col, Row, Select, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { stopAction } from "../../Actions/Index";
import { getTrainScheduleAction } from "../../Actions/LogicActions/TrainSchedule/GetTrainScheduleAction";
import LiveScheduleLocationPopup from "./LiveScheduleLocationPopup";
import LiveTrainScheduleTable, {
  TrainRecord,
  TrainStatus,
} from "../../Components/LiveTrainSchedule/LiveTrainScheduleTable";
import { _eventBus } from "../../core/events/EventBus";
import stopManager from "../../Lib/Utils/StopManager";
import { evtTree } from "../../Orchestrator/ConstantTree";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { StationsListType } from "../../Orchestrator/Types/Index";
import {
  ITrainScheduleItem,
  ITrainScheduleResponse,
} from "../../Orchestrator/Types/Interface/ITrainScheduleResponse";
import "./page.css";
import { convertTimeToArabic } from "../../Lib/Utils/validationUtil";

const PAGE_SIZE = 15;

// The schedule API works in GST (Asia/Dubai = UTC+4, no DST). Shift "now" by
// +4h and read the UTC fields to get GST wall-clock values.
const GST_OFFSET_MS = 4 * 60 * 60 * 1000;
const gstNow = () => new Date(Date.now() + GST_OFFSET_MS);

const todayParam = () =>
  gstNow().toISOString().split("T")[0].replace(/-/g, ""); // YYYYMMDD (GST date)

// The API's time filter doesn't resolve "now" server-side — the caller passes
// a concrete HH:MM:SS in GST. Sent as `arrival_from_time`, so it keeps trains
// arriving at or after it: in-transit (departed, not arrived) plus upcoming.
const nowGstTime = () => gstNow().toISOString().split("T")[1].slice(0, 8); // "HH:MM:SS"

// Total rows the backend says exist (before paging); falls back to what we got.
const totalFromResponse = (res: ITrainScheduleResponse | null): number => {
  const total = res?.pagination?.total;
  if (typeof total === "number") return total;
  return res?.trains?.length ?? 0;
};

// Train times come back as RFC3339 datetimes (e.g. "2026-05-11T08:15:00+04:00");
// a bare "HH:MM" is resolved against today as a fallback. Returns null when the
// value can't be parsed (or is the API's nullable estimate).
const resolveScheduleTimeEpoch = (time?: string | null): number | null => {
  if (!time) return null;
  if (/\d{4}-\d{2}-\d{2}/.test(time)) {
    const parsed = new Date(time).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }
  const match = time.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const d = new Date();
  d.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return d.getTime();
};

const LiveSchedulePage = () => {
  const intl = useIntl();
  const today = new Date();
  const formattedDate = new Intl.DateTimeFormat(intl.locale === "ar" ? "ar" : "en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(today);
  const [stops] = useRecState<StationsListType>("stopsResponseAtom");
  const [trainScheduleData] = useRecState<ITrainScheduleResponse>(
    "trainScheduleDataAtom"
  );
  const [selectedStation, setSelectedStation] = useState<string>("");
  const [selectedStationName, setSelectedStationName] = useState<string>("");
  // Unchecked (default) → not-yet-arrived trains only (arrival_from_time=now):
  // in-transit plus upcoming. Checked → the whole day, arrived trains included.
  const [seeAllTrains, setSeeAllTrains] = useState(false);
  // Backend paging, true incremental: we keep the rows fetched so far and each
  // "Load more" requests the next PAGE_SIZE at offset = current count.
  const [loadedTrains, setLoadedTrains] = useState<ITrainScheduleItem[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  const loadedCountRef = useRef(0);
  const [isLocationPopupOpen, setIsLocationPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    loadedCountRef.current = loadedTrains.length;
  }, [loadedTrains]);

  // Single place that talks to the schedule API. `notYetArrivedOnly` → send the
  // current GST time as `arrival_from_time` so the backend trims trains that
  // have already arrived (in-transit and upcoming ones stay); otherwise omit it
  // and get the whole day. Returns the raw response so callers decide whether
  // to replace or append.
  const requestTrains = useCallback(
    async (
      from: string,
      notYetArrivedOnly: boolean,
      limit: number,
      offset: number
    ): Promise<ITrainScheduleResponse | null> => {
      getTrainScheduleAction.setParams({
        date: todayParam(),
        from,
        to: "",
        limit,
        offset,
        arrivalFromTime: notYetArrivedOnly ? nowGstTime() : "",
      });
      await getTrainScheduleAction.execute();
      return getTrainScheduleAction.result;
    },
    []
  );

  // New filter (station or "see all trains" toggle) → forget what we'd loaded
  // and start from the first page; the fetch effect re-requests it.
  const resetPaging = () => {
    loadedCountRef.current = 0;
    setLoadedTrains([]);
    setServerTotal(0);
  };

  const handleStationChange = (id: string, name: string) => {
    if (id === selectedStation) return;
    setSelectedStation(id);
    setSelectedStationName(name);
    resetPaging();
  };

  const handleSeeAllTrainsChange = (checked: boolean) => {
    setSeeAllTrains(checked);
    resetPaging();
  };

  const handleLoadMore = async () => {
    if (isLoading || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const res = await requestTrains(
        selectedStation,
        !seeAllTrains,
        PAGE_SIZE,
        loadedTrains.length
      );
      if (res) {
        setLoadedTrains((prev) => {
          const seen = new Set(prev.map((t) => t.trip_id));
          const fresh = res.trains.filter((t) => !seen.has(t.trip_id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
        setServerTotal(totalFromResponse(res));
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  const [locations] = useRecState<StationsListType>("stopsResponseAtom");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".filter-controls")) {
        setIsLocationPopupOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!stops?.length) stopAction.execute();
  }, [stops]);

  useEffect(() => {
    if (!selectedStation) return;
    const name = stopManager.getStopNameById(selectedStation);
    setSelectedStationName(name);
  }, [selectedStation, intl.locale]);

  useEffect(() => {
    let cancelled = false;
    // Initial load (and the 2-min refresh): reload as many rows as we currently
    // show — at least one page — at offset 0, replacing the list. On a fresh
    // filter that's just the first page.
    const reload = async () => {
      setIsLoading(true);
      try {
        const limit = Math.max(PAGE_SIZE, loadedCountRef.current);
        const res = await requestTrains(
          selectedStation,
          !seeAllTrains,
          limit,
          0
        );
        if (cancelled) return;
        setLoadedTrains(res?.trains ?? []);
        setServerTotal(totalFromResponse(res));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    reload();
    const intervalId = setInterval(reload, 2 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [selectedStation, seeAllTrains, requestTrains]);

  useEffect(() => {
    if (trainScheduleData) {
      _eventBus.emit({
        eventName: evtTree.TopBar.Menu.Select,
        // data: trainScheduleData,
        data: { path: "live-schedule" },
      });
    }
  }, [trainScheduleData]);

  const trainData: TrainRecord[] = useMemo(() => {
    if (!loadedTrains.length) return [];

    return loadedTrains
      .map((train, idx): TrainRecord | null => {
        if (!train.departure_time || !train.arrival_time || !train.trip_id) {
          return null;
        }

        let status: TrainStatus = "OnTime";
        if (train.status === "delayed") status = "Late";
        else if (train.status === "cancelled") status = "Cancelled";

        const scheduledDeparture = convertTimeToArabic(train.departure_time);
        const scheduledArrival = convertTimeToArabic(train.arrival_time);

        const estimatedDeparture = convertTimeToArabic(
          train.estimated_departure_time || train.departure_time
        );
        const estimatedArrival = convertTimeToArabic(
          train.estimated_arrival_time || train.arrival_time
        );

        // Realtime estimates are per-leg and independent of the combined
        // `status`: a train that left on time can still pick up delay en route,
        // and GTFS-RT keeps refreshing the arrival estimate after departure.
        // So always prefer the estimate; renderTimeCell hides the scheduled
        // time when it matches. Cancelled trains keep the scheduled times.
        const isCancelled = status === "Cancelled";
        const departure = isCancelled ? scheduledDeparture : estimatedDeparture;
        const arrival = isCancelled ? scheduledArrival : estimatedArrival;
        const originalDeparture = scheduledDeparture;
        const originalArrival = scheduledArrival;

        // Schedule is for today only: once the effective departure time has
        // passed, the train counts as Departed (overriding On Time / Late).
        // A delayed train flips to Departed only when its delayed time passes;
        // cancelled trains stay cancelled.
        if (status !== "Cancelled") {
          const effectiveDepartureEpoch = resolveScheduleTimeEpoch(
            train.estimated_departure_time || train.departure_time
          );
          if (
            effectiveDepartureEpoch !== null &&
            effectiveDepartureEpoch <= Date.now()
          ) {
            status = "Departed";
          }
        }

        const details =
          status === "Cancelled"
            ? intl.formatMessage({ id: "scheduleCancelled" })
            : status === "Late"
            ? intl.formatMessage({ id: "scheduleLate" })
            : "";

        return {
          key: `${train.trip_id}_${idx}`,
          // The schedule API returns station ids as "<externalCode><platform>"
          // (e.g. "PMB1"), but StopManager is keyed by the external code ("PMB"),
          // so strip the trailing platform digits to resolve the localized name.
          // Falls back to the API's English station name when unresolved.
          // Note: getStopNameById reads the cookie locale (not intl); this stays
          // correct because switching language hard-reloads the page.
          from: stopManager.getStopNameById(
            (train.departure_station_id || "").replace(/\d+$/, ""),
            train.departure_station || ""
          ),
          to: stopManager.getStopNameById(
            (train.arrival_station_id || "").replace(/\d+$/, ""),
            train.arrival_station || ""
          ),
          departure,
          originalDeparture,
          arrival,
          originalArrival,
          trainNo: train.trip_short_name || train.trip_id,
          status,
          details,
        };
      })
      .filter((t): t is TrainRecord => t !== null)
      .sort((a, b) => a.departure.localeCompare(b.departure));
  }, [loadedTrains, intl]);

  // The backend already applies the not-yet-arrived ("see all trains" off)
  // filter and the paging, so there's more to fetch whenever it reports more.
  const hasMore = loadedTrains.length < serverTotal;

  const selectedStationLabel = selectedStation
    ? stopManager.getStopNameById(selectedStation)
    : "";

  const filterStops: SelectProps["options"] = useMemo(() => {
    const baseOptions: SelectProps["options"] = [
      {
        value: "",
        label: intl.formatMessage({ id: "scheduleAllStations" }),
      },
    ];

    if (stops?.length) {
      const stationOptions = stops.map((stop: any) => {
        const externalCode =
          Object.values(stop.externalCodes || {})[0] || stop.id;

        return {
          value: externalCode,
          label: stop.defaultName || stop.name || "",
        };
      });

      baseOptions.push(...stationOptions);
    }

    return baseOptions;
  }, [stops, intl]);

  let emptyMessage: string | undefined = undefined;

  // Only show an "empty" message once everything has settled and there's
  // genuinely nothing more to load — otherwise we're still fetching or the
  // table still renders a "Load more" button.
  if (!isLoading && !isLoadingMore && trainData.length === 0 && !hasMore) {
    if (selectedStation) {
      // A station is selected → always show the station-specific message,
      // whether or not "see all trains" is ticked.
      emptyMessage = intl.formatMessage(
        { id: "noTrainsDepartingFrom" },
        { stopName: selectedStationLabel }
      );
    } else if (!seeAllTrains) {
      // No station, not-yet-arrived view → no more trains running today.
      emptyMessage = intl.formatMessage({ id: "scheduleNoUpcomingTrains" });
    } else {
      emptyMessage = intl.formatMessage({ id: "liveStatusUnavailable" });
    }
  }

  return (
    <div className="live-schedule-bg">
      <div className="main-section-live-schedule container">
        <Row
          gutter={[16, 16]}
          className="live-schedule-title-row"
          align="middle"
          justify="space-between"
        >
          <Col xs={24} sm={24} xl={{ flex: "auto" }}>
            <Typography.Title
              level={3}
              className="text-capitalize mb-0 ff-inter schedule-title"
            >
              {intl.formatMessage({ id: "scheduleTitle" })}
            </Typography.Title>
            <Typography.Paragraph className="mb-0 ff-inter schedule-subtitle">
              {intl.formatMessage(
                { id: "scheduleSubtitle" },
                { date: formattedDate }
              )}
            </Typography.Paragraph>
          </Col>

          <Col
            xs={24}
            sm={24}
            xl={{ flex: "auto" }}
            className="filter-controls-col"
          >
            <div
              className="filter-controls"
              style={{ display: "flex", gap: 16, alignItems: "center" }}
            >
              <div className="filter-controls-dropwon" style={{ position: "relative" }}>
                <Select
                  value={selectedStationName}
                  className="ff-inter schedule-filter"
                  onClick={() => setIsLocationPopupOpen(true)}
                  onChange={(value, option) => {
                    const name =
                      !Array.isArray(option) && typeof option?.label === "string"
                        ? option.label
                        : !Array.isArray(option)
                        ? option?.label?.toString() || ""
                        : "";
                    handleStationChange(value, name);
                    setIsLocationPopupOpen(false);
                  }}
                  style={{ width: 200 }}
                  options={filterStops}
                  optionLabelProp="label"
                  suffixIcon={
                    <CardChevron direction="down" width={10} height={6} />
                  }
                  styles={{ popup: { root: { display: "none" } } }}
                />
                <LiveScheduleLocationPopup
                  locations={locations}
                  visible={isLocationPopupOpen}
                  onSelect={(id: string, name: string) => {
                    handleStationChange(id, name);
                    setIsLocationPopupOpen(false);
                  }}
                  showAllStations={true}
                />
              </div>

              <div className="see-all-trains-box">
                <Checkbox
                  className="ff-inter see-all-trains-checkbox"
                  checked={seeAllTrains}
                  onChange={(e) => handleSeeAllTrainsChange(e.target.checked)}
                >
                  {intl.formatMessage({ id: "scheduleSeeAllTrains" })}
                </Checkbox>
              </div>
            </div>
          </Col>
        </Row>

        <LiveTrainScheduleTable
          data={trainData}
          emptyMessage={emptyMessage}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
        />
      </div>
    </div>
  );
};

export default LiveSchedulePage;
